import React, { useEffect, useState } from 'react';
import { Sparkles, Edit2, Trash2, Save, CheckCircle, Clock, History, Plus, Eye, RotateCcw } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';


interface RiskAppetiteKPI {
  id: string;
  categoryId: string;
  categoryName: string;
  name: string;
  description: string;
  // RAG (Red/Amber/Green) Thresholds
  threshold: number; // Keep for backward compatibility (represents Red threshold)
  thresholdGreen?: number; // Green threshold (good/acceptable)
  thresholdAmber?: number; // Amber threshold (warning)
  thresholdRed?: number; // Red threshold (critical)
  unit: string;
  operator: 'greater-than' | 'less-than' | 'equals' | 'between';
  severity: 'critical' | 'high' | 'medium' | 'low';
  action: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  isManuallyEdited?: boolean;
}

interface RiskAppetiteVersion {
  id: string;
  version: number;
  statement: string;
  kpis: RiskAppetiteKPI[];
  status: 'draft' | 'approved' | 'superseded';
  createdBy: string;
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
  effectiveDate?: string;
  supersededAt?: string;
  notes?: string;
}

interface RiskAppetiteStatement {
  id: string;
  statement: string;
  generatedAt: string;
  generatedBy: string;
  isActive: boolean;
  kpis: RiskAppetiteKPI[];
  status?: 'draft' | 'approved' | 'superseded';
  version?: number;
}

const RiskAppetitePage: React.FC = () => {
  const [appetiteStatement, setAppetiteStatement] = useState('');
  const [generatedAppetite, setGeneratedAppetite] = useState<RiskAppetiteStatement | null>(null);
  const [versionHistory, setVersionHistory] = useState<RiskAppetiteVersion[]>([]);
  const [showHistory, setShowHistory] = useState(true);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [editingKPI, setEditingKPI] = useState<RiskAppetiteKPI | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<RiskAppetiteVersion | null>(null);
  const [preserveEdited, setPreserveEdited] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    loadData();
    loadVersionHistory();
  }, []);

  const loadData = async () => {
    try {
      // Get all versions and find the approved one (or most recent draft)
      const appetiteRes = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/versions`);
      if (appetiteRes.ok) {
        const data = await appetiteRes.json();
        if (data.success && data.data && data.data.length > 0) {
          // First, try to find an approved version
          let versionToLoad = data.data.find((v: any) => v.status === 'approved');
          
          // If no approved version, use the most recent version (highest version number)
          if (!versionToLoad) {
            versionToLoad = data.data.reduce((latest: any, current: any) => {
              return current.version > latest.version ? current : latest;
            }, data.data[0]);
          }
          
          if (versionToLoad) {
            setGeneratedAppetite(versionToLoad);
            setAppetiteStatement(versionToLoad.statement);
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const loadVersionHistory = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/versions`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setVersionHistory(data.data);
        }
      }
    } catch (err) {
      console.error('Error loading version history:', err);
    }
  };

  const approveDraft = async () => {
    if (!generatedAppetite || generatedAppetite.status !== 'draft') {
      setError('Only draft versions can be approved');
      return;
    }

    setIsApproving(true);
    setError(null);
    setShowApproveModal(false);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionId: generatedAppetite.id,
          approvedBy: 'Sarah Chen'
        })
      });

      const data = await response.json();
      if (data.success) {
        // Reload data to show approved version
        await loadData();
        await loadVersionHistory();
        setError(null);
      } else {
        setError(data.error || 'Failed to approve draft');
      }
    } catch (err) {
      setError('Failed to connect to approval service');
      console.error(err);
    } finally {
      setIsApproving(false);
    }
  };

  const saveStatement = async () => {
    if (!appetiteStatement.trim()) {
      setError('Statement cannot be empty');
      return;
    }

    if (!generatedAppetite || !generatedAppetite.kpis || generatedAppetite.kpis.length === 0) {
      setError('Please generate KPIs first using "Generate KPIs with Themis" button');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Update the existing draft version (don't create new or approve)
      if (generatedAppetite.status === 'draft') {
        // Update existing draft
        const response = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/versions?id=${generatedAppetite.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            statement: appetiteStatement,
            kpis: generatedAppetite.kpis
          })
        });

        const data = await response.json();
        if (data.success) {
          setGeneratedAppetite({
            ...generatedAppetite,
            statement: appetiteStatement,
            kpis: generatedAppetite.kpis
          });
          setHasUnsavedChanges(false);
          await loadVersionHistory();
        } else {
          setError(data.error || 'Failed to update draft');
        }
      } else {
        // Create new draft version if current is approved
        const response = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/versions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            statement: appetiteStatement,
            kpis: generatedAppetite.kpis,
            createdBy: 'Sarah Chen',
            notes: 'Manual update via UI'
          })
        });

        const data = await response.json();
        if (data.success) {
          setGeneratedAppetite({
            id: data.data.id,
            statement: data.data.statement,
            generatedAt: data.data.createdAt,
            generatedBy: data.data.createdBy,
            isActive: false,
            kpis: data.data.kpis,
            status: 'draft',
            version: data.data.version
          });
          setHasUnsavedChanges(false);
          await loadVersionHistory();
        } else {
          setError(data.error || 'Failed to save statement');
        }
      }
    } catch (err) {
      setError('Failed to save statement');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const generateWithThemis = async () => {
    if (!appetiteStatement.trim()) {
      setError('Please enter a risk appetite statement');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Get manually edited KPIs if preserve is enabled
      const manuallyEditedKPIs = preserveEdited && generatedAppetite
        ? generatedAppetite.kpis.filter(kpi => kpi.isManuallyEdited)
        : [];

      // Step 1: Generate KPIs using Themis AI
      const themisResponse = await fetch(`${import.meta.env.VITE_API_URL}/themis/recommendations/kpis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statement: appetiteStatement })
      });

      const themisData = await themisResponse.json();
      if (!themisData.success) {
        setError(themisData.error || 'Failed to generate KPIs with Themis');
        return;
      }

      // Merge AI-generated KPIs with preserved manually edited ones
      const mergedKPIs = [...themisData.data.kpis, ...manuallyEditedKPIs];

      // Step 2: Create and auto-approve version with generated KPIs
      const createResponse = await fetch(`${import.meta.env.VITE_API_URL}/risk-appetite/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statement: appetiteStatement,
          kpis: mergedKPIs,
          createdBy: 'Sarah Chen',
          notes: preserveEdited && manuallyEditedKPIs.length > 0
            ? `Generated with Themis AI (${manuallyEditedKPIs.length} manually edited KPIs preserved)`
            : 'Generated with Themis AI'
        })
      });

      const createData = await createResponse.json();
      if (createData.success) {
        // Draft created successfully - show generated KPIs for review
        setGeneratedAppetite({
          id: createData.data.id,
          statement: createData.data.statement,
          generatedAt: createData.data.createdAt,
          generatedBy: createData.data.createdBy,
          isActive: createData.data.status === 'approved', // Will be false (draft status)
          kpis: createData.data.kpis,
          status: createData.data.status,
          version: createData.data.version
        });
        setHasUnsavedChanges(false);
        // Only reload version history, not the current version (we already have it)
        await loadVersionHistory();
        
        // Show success message that draft was created
        console.log('Draft version created successfully:', createData.data);
      } else {
        setError(createData.error || 'Failed to create draft version');
      }
    } catch (err) {
      setError('Failed to connect to Themis AI');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const deleteKPI = async (kpiId: string) => {
    if (!generatedAppetite) return;

    // Remove the KPI from the current draft
    const updatedKPIs = generatedAppetite.kpis.filter(kpi => kpi.id !== kpiId);
    setGeneratedAppetite({
      ...generatedAppetite,
      kpis: updatedKPIs
    });
    setHasUnsavedChanges(true);
  };

  const startEditKPI = (kpi: RiskAppetiteKPI) => {
    setEditingKPI(kpi);
  };

  const saveEditedKPI = () => {
    if (!editingKPI || !generatedAppetite) return;

    // Update the KPI in the current draft
    const updatedKPIs = generatedAppetite.kpis.map(kpi =>
      kpi.id === editingKPI.id ? editingKPI : kpi
    );

    setGeneratedAppetite({
      ...generatedAppetite,
      kpis: updatedKPIs
    });
    setEditingKPI(null);
    setHasUnsavedChanges(true);
  };

  const addManualKPI = () => {
    if (!generatedAppetite) return;

    const now = new Date().toISOString();
    const newKPI: RiskAppetiteKPI = {
      id: `kpi-${Date.now()}`,
      categoryId: 'custom',
      categoryName: 'Custom',
      name: 'New KPI',
      description: 'Add description',
      threshold: 0,
      unit: '%',
      operator: 'greater-than',
      severity: 'medium',
      action: 'Monitor and review',
      isActive: true,
      createdAt: now,
      updatedAt: now,
      isManuallyEdited: true
    };

    setGeneratedAppetite({
      ...generatedAppetite,
      kpis: [...generatedAppetite.kpis, newKPI]
    });
    setEditingKPI(newKPI);
    setHasUnsavedChanges(true);
  };

  const getSeverityColor = (severity: string) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[severity as keyof typeof colors] || colors.medium;
  };

  const getOperatorSymbol = (operator: string) => {
    const symbols = {
      'greater-than': '>',
      'less-than': '<',
      'equals': '=',
      'between': '↔'
    };
    return symbols[operator as keyof typeof symbols] || operator;
  };

  const formatRAGThresholds = (kpi: RiskAppetiteKPI) => {
    // If RAG thresholds are defined, show them
    if (kpi.thresholdGreen !== undefined || kpi.thresholdAmber !== undefined || kpi.thresholdRed !== undefined) {
      return (
        <div className="flex items-center gap-2 flex-wrap">
          {kpi.thresholdGreen !== undefined && (
            <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400 border border-green-500/30">
              🟢 {getOperatorSymbol(kpi.operator)} {kpi.thresholdGreen}{kpi.unit}
            </span>
          )}
          {kpi.thresholdAmber !== undefined && (
            <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
              🟡 {getOperatorSymbol(kpi.operator)} {kpi.thresholdAmber}{kpi.unit}
            </span>
          )}
          {(kpi.thresholdRed !== undefined || kpi.threshold) && (
            <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 border border-red-500/30">
              🔴 {getOperatorSymbol(kpi.operator)} {kpi.thresholdRed ?? kpi.threshold}{kpi.unit}
            </span>
          )}
        </div>
      );
    }
    
    // Fallback to legacy single threshold
    return (
      <span className="text-primary-400 font-mono font-semibold">
        {getOperatorSymbol(kpi.operator)} {kpi.threshold}{kpi.unit}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Risk Appetite</h1>
        <p className="text-neutral-400 mt-2">
          Define your organizational risk tolerance and let Themis AI generate monitoring thresholds
        </p>
      </div>

      <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-xl font-semibold text-white">Risk Appetite Statement</h2>
          <Sparkles className="w-5 h-5 text-primary-400" />
        </div>
        
        <p className="text-sm text-neutral-400 mb-4">
          Describe your organization's risk tolerance in natural language. Themis will analyze your statement
          and recommend relevant KPIs and monitoring thresholds.
        </p>

        <textarea
          className="w-full h-32 px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          placeholder="Example: We are a conservative financial institution with zero tolerance for compliance breaches. We prioritize employee wellbeing and maintain staff utilization below 75%. Single points of failure are not acceptable for critical skills..."
          value={appetiteStatement}
          onChange={(e) => {
            setAppetiteStatement(e.target.value);
            setHasUnsavedChanges(true);
          }}
        />

        {error && (
          <div className="mt-3 p-3 bg-red-950/30 border border-red-500/30 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mt-4">
          <Button
            onClick={saveStatement}
            disabled={isSaving || !hasUnsavedChanges}
            className="bg-neutral-800 hover:bg-neutral-700 text-white font-medium disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {hasUnsavedChanges ? 'Save Statement' : 'Statement Saved'}
              </>
            )}
          </Button>

          <div className="flex items-center gap-4">
            <Button
              onClick={generateWithThemis}
              disabled={isGenerating || !appetiteStatement.trim()}
              className="bg-primary-500 hover:bg-primary-600 text-black font-medium"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-2" />
                  Analyzing with Themis AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generatedAppetite ? 'Regenerate' : 'Generate'} KPIs with Themis
                </>
              )}
            </Button>

            {generatedAppetite && generatedAppetite.kpis.some(kpi => kpi.isManuallyEdited) && (
              <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preserveEdited}
                  onChange={(e) => setPreserveEdited(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-neutral-950"
                />
                Preserve manually edited KPIs
              </label>
            )}
          </div>

          {generatedAppetite && (
            <span className="text-sm text-neutral-400">
              Last generated: {new Date(generatedAppetite.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {!generatedAppetite && !isGenerating && (
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-neutral-900 rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-neutral-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">No Risk Appetite Defined</h3>
              <p className="text-neutral-400 text-sm max-w-md mx-auto">
                Get started by entering your organization's risk tolerance statement above, then click "Generate KPIs with Themis" to create your first version.
              </p>
            </div>
          </div>
        </div>
      )}

      {generatedAppetite && generatedAppetite.kpis.length > 0 && (
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-white">Risk Appetite KPIs</h2>
                  {generatedAppetite.status === 'draft' && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      <Clock className="w-3 h-3 mr-1" />
                      Draft v{generatedAppetite.version || 1}
                    </Badge>
                  )}
                  {generatedAppetite.status === 'approved' && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved v{generatedAppetite.version || 1}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-neutral-400 mt-1">
                  {generatedAppetite.kpis.length} monitoring thresholds generated by Themis
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={addManualKPI}
                disabled={generatedAppetite.status !== 'draft'}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add KPI
              </Button>
              {generatedAppetite.status === 'draft' && (
                <Button 
                  onClick={() => setShowApproveModal(true)}
                  disabled={isApproving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isApproving ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Draft
                    </>
                  )}
                </Button>
              )}
              <Button 
                onClick={() => setShowHistory(!showHistory)}
                className="bg-neutral-800 hover:bg-neutral-700 text-white"
              >
                <History className="w-4 h-4 mr-2" />
                {showHistory ? 'Hide' : 'Show'} History
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-3 text-sm font-semibold text-neutral-400">Category</th>
                  <th className="text-left py-3 px-3 text-sm font-semibold text-neutral-400">KPI Name</th>
                  <th className="text-left py-3 px-3 text-sm font-semibold text-neutral-400">Threshold</th>
                  <th className="text-left py-3 px-3 text-sm font-semibold text-neutral-400">Severity</th>
                  <th className="text-left py-3 px-3 text-sm font-semibold text-neutral-400">Action</th>
                  <th className="text-right py-3 px-3 text-sm font-semibold text-neutral-400">Operations</th>
                </tr>
              </thead>
              <tbody>
                {generatedAppetite.kpis.map((kpi) => (
                  <tr key={kpi.id} className="border-b border-neutral-800 hover:bg-neutral-900/50">
                    <td className="py-3 px-3">
                      <span className="text-sm text-neutral-300">{kpi.categoryName}</span>
                    </td>
                    <td className="py-3 px-3">
                      <div>
                        <p className="text-white font-medium">{kpi.name}</p>
                        <p className="text-xs text-neutral-400 mt-1">{kpi.description}</p>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      {formatRAGThresholds(kpi)}
                    </td>
                    <td className="py-3 px-3">
                      <Badge className={getSeverityColor(kpi.severity)}>
                        {kpi.severity.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm text-neutral-300">{kpi.action}</span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEditKPI(kpi)}
                          className="p-2 hover:bg-neutral-800 rounded text-neutral-400 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteKPI(kpi.id)}
                          className="p-2 hover:bg-red-950/30 rounded text-neutral-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Version History Section */}
      {showHistory && versionHistory.length > 0 && (
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-primary-400" />
            <h2 className="text-xl font-semibold text-white">Version History</h2>
            <Badge className="bg-neutral-800 text-neutral-300">
              {versionHistory.length} versions
            </Badge>
          </div>

          <div className="space-y-3">
            {versionHistory.map((version) => (
              <div
                key={version.id}
                className={`p-4 rounded-lg border ${
                  version.status === 'approved'
                    ? 'bg-green-950/20 border-green-500/30'
                    : version.status === 'draft'
                    ? 'bg-yellow-950/20 border-yellow-500/30'
                    : 'bg-neutral-900 border-neutral-800'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white font-semibold">Version {version.version}</span>
                      {version.status === 'approved' && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approved
                        </Badge>
                      )}
                      {version.status === 'draft' && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <Clock className="w-3 h-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                      {version.status === 'superseded' && (
                        <Badge className="bg-neutral-700 text-neutral-400 border-neutral-600">
                          Superseded
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-300 mb-2 line-clamp-2">
                      {version.statement}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-neutral-400">
                      <span>Created by {version.createdBy}</span>
                      <span>•</span>
                      <span>{new Date(version.createdAt).toLocaleString()}</span>
                      {version.approvedBy && (
                        <>
                          <span>•</span>
                          <span>Approved by {version.approvedBy}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{version.kpis.length} KPIs</span>
                    </div>
                    {version.notes && (
                      <p className="text-xs text-neutral-500 mt-2 italic">
                        Note: {version.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setSelectedVersion(version)}
                      className="bg-primary-600 hover:bg-primary-700 text-white text-sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                    {version.status === 'draft' && version.id !== generatedAppetite?.id && (
                      <Button
                        onClick={async () => {
                          // Load this draft version
                          setGeneratedAppetite({
                            id: version.id,
                            statement: version.statement,
                            generatedAt: version.createdAt,
                            generatedBy: version.createdBy,
                            isActive: false,
                            kpis: version.kpis,
                            status: version.status,
                            version: version.version
                          });
                          setAppetiteStatement(version.statement);
                        }}
                        className="bg-neutral-800 hover:bg-neutral-700 text-white text-sm"
                      >
                        Load Draft
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Version Details Slide-out Panel */}
      <Sheet open={!!selectedVersion} onOpenChange={(open) => !open && setSelectedVersion(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedVersion && (
            <>
              <SheetHeader>
                <SheetTitle>Version {selectedVersion.version} Details</SheetTitle>
                <SheetDescription>
                  Created {new Date(selectedVersion.createdAt).toLocaleString()}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status Badge */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-400 mb-2">Status</h3>
                  {selectedVersion.status === 'approved' && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </Badge>
                  )}
                  {selectedVersion.status === 'draft' && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      <Clock className="w-3 h-3 mr-1" />
                      Draft
                    </Badge>
                  )}
                  {selectedVersion.status === 'superseded' && (
                    <Badge className="bg-neutral-700 text-neutral-400 border-neutral-600">
                      Superseded
                    </Badge>
                  )}
                </div>

                {/* Risk Appetite Statement */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-400 mb-2">Risk Appetite Statement</h3>
                  <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-4">
                    <p className="text-white">{selectedVersion.statement}</p>
                  </div>
                </div>

                {/* Metadata */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-400 mb-2">Metadata</h3>
                  <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400">Created By</span>
                      <span className="text-white">{selectedVersion.createdBy}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400">Created At</span>
                      <span className="text-white">{new Date(selectedVersion.createdAt).toLocaleString()}</span>
                    </div>
                    {selectedVersion.approvedBy && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-400">Approved By</span>
                          <span className="text-white">{selectedVersion.approvedBy}</span>
                        </div>
                        {selectedVersion.approvedAt && (
                          <div className="flex items-center justify-between">
                            <span className="text-neutral-400">Approved At</span>
                            <span className="text-white">{new Date(selectedVersion.approvedAt).toLocaleString()}</span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedVersion.notes && (
                      <div className="pt-2 border-t border-neutral-800">
                        <span className="text-neutral-400 block mb-1">Notes</span>
                        <span className="text-white italic">{selectedVersion.notes}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* KPIs */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-400 mb-2">
                    Key Performance Indicators ({selectedVersion.kpis.length})
                  </h3>
                  <div className="space-y-3">
                    {selectedVersion.kpis.map((kpi) => (
                      <div key={kpi.id} className="bg-neutral-950 rounded-lg border border-neutral-800 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-white font-medium">{kpi.name}</h4>
                            <p className="text-xs text-neutral-400 mt-1">{kpi.description}</p>
                          </div>
                          <Badge className={getSeverityColor(kpi.severity)}>
                            {kpi.severity.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                          <div>
                            <span className="text-neutral-400">Category</span>
                            <p className="text-white">{kpi.categoryName}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-neutral-400 block mb-2">Thresholds (RAG)</span>
                            {formatRAGThresholds(kpi)}
                          </div>
                          <div className="col-span-2">
                            <span className="text-neutral-400">Action</span>
                            <p className="text-white">{kpi.action}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t border-neutral-800">
                  <Button
                    onClick={() => {
                      // Create new draft from this version
                      setGeneratedAppetite({
                        id: selectedVersion.id,
                        statement: selectedVersion.statement,
                        generatedAt: selectedVersion.createdAt,
                        generatedBy: selectedVersion.createdBy,
                        isActive: false,
                        kpis: selectedVersion.kpis,
                        status: 'draft',
                        version: (versionHistory.length || 1) + 1
                      });
                      setAppetiteStatement(selectedVersion.statement);
                      setSelectedVersion(null);
                    }}
                    className="bg-primary-600 hover:bg-primary-700 text-white flex-1"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore as New Draft
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Approve Draft Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Risk Appetite Draft</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this draft? This will make it the active risk appetite statement and automatically supersede any previous versions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowApproveModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={approveDraft}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit KPI Modal */}
      <Dialog open={!!editingKPI} onOpenChange={(open) => !open && setEditingKPI(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit KPI</DialogTitle>
            <DialogDescription>
              Modify the KPI details below. Changes will be saved to the current draft.
            </DialogDescription>
          </DialogHeader>

          {editingKPI && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">KPI Name</label>
                <input
                  type="text"
                  value={editingKPI.name}
                  onChange={(e) => setEditingKPI({ ...editingKPI, name: e.target.value })}
                  className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Description</label>
                <textarea
                  value={editingKPI.description}
                  onChange={(e) => setEditingKPI({ ...editingKPI, description: e.target.value })}
                  className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Category</label>
                  <input
                    type="text"
                    value={editingKPI.categoryName}
                    onChange={(e) => setEditingKPI({ ...editingKPI, categoryName: e.target.value })}
                    className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Severity</label>
                  <select
                    value={editingKPI.severity}
                    onChange={(e) => setEditingKPI({ ...editingKPI, severity: e.target.value as 'critical' | 'high' | 'medium' | 'low' })}
                    className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              {/* RAG (Red/Amber/Green) Thresholds */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-3">
                  RAG Thresholds (Red/Amber/Green)
                </label>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-neutral-400 mb-2">Operator</label>
                    <select
                      value={editingKPI.operator}
                      onChange={(e) => setEditingKPI({ ...editingKPI, operator: e.target.value as 'greater-than' | 'less-than' | 'equals' | 'between' })}
                      className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="greater-than">Greater Than</option>
                      <option value="less-than">Less Than</option>
                      <option value="equals">Equals</option>
                      <option value="between">Between</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-green-400 mb-2">🟢 Green (Good)</label>
                    <input
                      type="number"
                      value={editingKPI.thresholdGreen ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        if (value !== undefined && !isNaN(value)) {
                          setEditingKPI({ ...editingKPI, thresholdGreen: value });
                        } else {
                          const { thresholdGreen, ...rest } = editingKPI;
                          setEditingKPI(rest as RiskAppetiteKPI);
                        }
                      }}
                      placeholder="e.g. 75"
                      className="w-full px-4 py-2 bg-neutral-950 border border-green-700/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-yellow-400 mb-2">🟡 Amber (Warning)</label>
                    <input
                      type="number"
                      value={editingKPI.thresholdAmber ?? ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        if (value !== undefined && !isNaN(value)) {
                          setEditingKPI({ ...editingKPI, thresholdAmber: value });
                        } else {
                          const { thresholdAmber, ...rest } = editingKPI;
                          setEditingKPI(rest as RiskAppetiteKPI);
                        }
                      }}
                      placeholder="e.g. 85"
                      className="w-full px-4 py-2 bg-neutral-950 border border-yellow-700/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-red-400 mb-2">🔴 Red (Critical)</label>
                    <input
                      type="number"
                      value={editingKPI.thresholdRed ?? editingKPI.threshold}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setEditingKPI({ 
                          ...editingKPI, 
                          thresholdRed: value,
                          threshold: value // Keep legacy threshold in sync
                        });
                      }}
                      placeholder="e.g. 95"
                      className="w-full px-4 py-2 bg-neutral-950 border border-red-700/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Unit: {editingKPI.unit || '%'} • Example: For utilization &lt; 75% = Green, 75-85% = Amber, &gt; 85% = Red
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Unit</label>
                <input
                  type="text"
                  value={editingKPI.unit}
                  onChange={(e) => setEditingKPI({ ...editingKPI, unit: e.target.value })}
                  className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Action</label>
                <input
                  type="text"
                  value={editingKPI.action}
                  onChange={(e) => setEditingKPI({ ...editingKPI, action: e.target.value })}
                  className="w-full px-4 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setEditingKPI(null)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditedKPI}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskAppetitePage;
